import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Service responsible for providing form validation functionality across the application.
 * Contains reusable validation patterns and error messages for various form fields.
 */
@Injectable({
	providedIn: 'root',
})
export class ValidationService {
	// Regular expression patterns for different types of validations
	private readonly emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	private readonly passwordPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$/;
	private readonly nifPattern = /^[0-9]{8}[A-Z]$/; // Spanish NIF: 8 digits + 1 uppercase letter
	private readonly niePattern = /^[XYZ][0-9]{7}[A-Z]$/; // Spanish NIE: X/Y/Z + 7 digits + 1 uppercase letter
	private readonly cifPattern = /^[A-Z][0-9]{7}[0-9A-Z]$/; // Spanish CIF: 1 letter + 7 digits + 1 letter/number
	private readonly phonePattern = /^[0-9]{3}-[0-9]{3}-[0-9]{3}$|^[0-9]{9}$/; // Spanish phone number: 111-111-111 or 111111111
	private readonly cardNumberPattern = /^[0-9]{16}$/; // Credit card number: 16 digits
	private readonly expirationDatePattern = /^(0[1-9]|1[0-2])\/([0-9]{2})$/; // MM/YY format
	private readonly cvcPattern = /^[0-9]{3,4}$/; // CVC: 3 or 4 digits
	private readonly postalCodePattern = /^[0-9]{5}$/; // Spanish postal code: 5 digits

	/**
	 * Dictionary containing validation error messages for different validation types.
	 * Each key corresponds to a specific validation error, and the value is the user-friendly message.
	 */
	private validationMessages: { [key: string]: string } = {
		required: 'Este campo es obligatorio.',
		invalidLogin: 'Usuario o contraseña incorrectos.',
		invalidEmailAddress: 'El correo electrónico no tiene un formato válido.',
		invalidPassword: 'Contraseña inválida',
		passwordsDoNotMatch: 'Las contraseñas no coinciden.',
		termsRequired: 'Debes aceptar los términos y condiciones.',
		pepRequired: 'Debes confirmar que no eres un PEP o relacionado.',
		invalidNIF: 'NIF inválido (8 números y 1 letra).',
		invalidCIF: 'CIF inválido (1 letra - 7 números - 1 letra(o numero de control)).',
		invalidPhone: 'Teléfono inválido (9 números).',
		invalidCardNumber: 'Número de tarjeta inválido (16 números).',
		invalidExpirationDate: 'Fecha de expiración inválida o tarjeta expirada (MM/YY).',
		invalidCVC: 'CVC inválido (3 o 4 dígitos).',
		invalidPostalCode: 'Código postal inválido (5 dígitos entre 01000 y 52999).',
		integer: 'El valor debe ser un número entero.',
	};

	/**
	 * Retrieves the error message for a given validator name.
	 * @param validatorName - The name of the validator
	 * @returns The corresponding error message or a default message if not found
	 */
	getValidatorErrorMessage(validatorName: string): string {
		return this.validationMessages[validatorName] || 'Error desconocido.';
	}

	/**
	 * Validates email format using a regular expression pattern.
	 * @returns A validator function that checks if the email format is valid
	 */
	emailValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (value && !this.emailPattern.test(value)) {
				return { invalidEmailAddress: true };
			}
			return null;
		};
	}

	/**
	 * Validates password strength using a regular expression pattern.
	 * Password must contain at least:
	 * - One uppercase letter
	 * - One lowercase letter
	 * - One number
	 * - One special character
	 * - Minimum 8 characters
	 * @returns A validator function that checks if the password meets the requirements
	 */
	passwordValidator() {
		return (control: AbstractControl): { [key: string]: boolean } | null => {
			if (control.value && this.passwordPattern.test(control.value)) {
				return null; // Valid
			}
			return { invalidPassword: true }; // Invalid
		};
	}

	/**
	 * Retrieves tooltip messages for password requirements.
	 * @param key - The key identifying the tooltip message
	 * @returns The formatted HTML message for the tooltip
	 */
	getTooltipMessage(key: string): string {
		const messages: { [key: string]: string } = {
			invalidPassword: `
			La contraseña debe tener al menos:
			<ul style="margin: 0; padding-left: 1rem">
			  <li>8 caracteres</li>
			  <li>Una mayúscula</li>
			  <li>Una minúscula</li>
			  <li>Un número</li>
			  <li>Un carácter especial</li>
			</ul>
		  `,
		};

		return messages[key] || '';
	}

	/**
	 * Validates that two password fields match.
	 * @param passwordFieldName - The name of the password field to compare against
	 * @returns A validator function that checks if the passwords match
	 */
	confirmPasswordValidator(passwordFieldName: string) {
		return (control: AbstractControl): { [key: string]: boolean } | null => {
			const formGroup = control.parent;
			if (formGroup) {
				const passwordControl = formGroup.get(passwordFieldName);
				if (passwordControl && passwordControl.value !== control.value) {
					return { passwordsDoNotMatch: true };
				}
			}
			return null;
		};
	}

	/**
	 * Validates that terms and conditions have been accepted.
	 * @returns A validator function that checks if terms are accepted
	 */
	termsValidator() {
		return (control: AbstractControl): { [key: string]: boolean } | null => {
			return control.value ? null : { termsRequired: true };
		};
	}

	/**
	 * Validates that PEP (Politically Exposed Person) status has been confirmed.
	 * @returns A validator function that checks if PEP status is confirmed
	 */
	pepValidator() {
		return (control: AbstractControl): { [key: string]: boolean } | null => {
			return control.value ? null : { pepRequired: true };
		};
	}

	/**
	 * Validates Spanish NIF (Tax Identification Number) or NIE (Foreigner Identification Number).
	 * NIF format: 8 digits + 1 uppercase letter
	 * NIE format: X/Y/Z + 7 digits + 1 uppercase letter
	 * @returns A validator function that checks if the NIF/NIE format is valid
	 */
	nifValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;

			const upperValue = value.toUpperCase();

			if (!this.nifPattern.test(upperValue) && !this.niePattern.test(upperValue)) {
				return { invalidNIF: true };
			}

			return null;
		};
	}

	/**
	 * Validates Spanish CIF (Company Identification Number).
	 * Format: 1 letter + 7 digits + 1 letter/number
	 * @returns A validator function that checks if the CIF format is valid
	 */
	cifValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;

			const upperValue = value.toUpperCase();

			if (!this.cifPattern.test(upperValue)) {
				return { invalidCIF: true };
			}

			return null;
		};
	}

	/**
	 * Validates Spanish phone number format.
	 * Format: 111-111-111
	 * @returns A validator function that checks if the phone number format is valid
	 */
	phoneValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;

			if (!this.phonePattern.test(value)) {
				return { invalidPhone: true };
			}

			return null;
		};
	}

	/**
	 * Validates credit card number using Luhn algorithm.
	 * @returns A validator function that checks if the card number is valid
	 */
	cardNumberValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value?.replace(/\s/g, '');
			if (!value) return null;

			// Luhn algorithm for card number validation
			let sum = 0;
			let isEven = false;

			for (let i = value.length - 1; i >= 0; i--) {
				let digit = Number.parseInt(value.charAt(i), 10);

				if (isEven) {
					digit *= 2;
					if (digit > 9) {
						digit -= 9;
					}
				}

				sum += digit;
				isEven = !isEven;
			}

			if (sum % 10 !== 0) {
				return { invalidCardNumber: true };
			}

			return null;
		};
	}

	/**
	 * Validates credit card expiration date.
	 * Format: MM/YY
	 * Also checks if the card is not expired
	 * @returns A validator function that checks if the expiration date is valid
	 */
	expirationDateValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;

			const match = value.match(this.expirationDatePattern);
			if (!match) {
				return { invalidExpirationDate: true };
			}

			const month = Number.parseInt(match[1], 10);
			const year = Number.parseInt(match[2], 10);

			// Validate month
			if (month < 1 || month > 12) {
				return { invalidExpirationDate: true };
			}

			// Validate not expired
			const currentDate = new Date();
			const currentYear = currentDate.getFullYear() % 100;
			const currentMonth = currentDate.getMonth() + 1;

			if (year < currentYear || (year === currentYear && month < currentMonth)) {
				return { invalidExpirationDate: true };
			}

			return null;
		};
	}

	/**
	 * Validates CVC (Card Verification Code).
	 * Format: 3 or 4 digits
	 * @returns A validator function that checks if the CVC format is valid
	 */
	cvcValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;

			if (!this.cvcPattern.test(value)) {
				return { invalidCVC: true };
			}

			return null;
		};
	}

	/**
	 * Validates that a value is an integer.
	 * @returns A validator function that checks if the value is an integer
	 */
	static integerValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			return !Number.isInteger(+value) ? { integer: true } : null;
		};
	}

	/**
	 * Validates Spanish postal code format.
	 * Format: 5 digits between 01000 and 52999
	 * @returns A validator function that checks if the postal code is valid
	 */
	postalCodeValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;

			if (!this.postalCodePattern.test(value)) {
				return { invalidPostalCode: true };
			}

			const postalCode = Number.parseInt(value, 10);
			if (postalCode < 1000 || postalCode > 52999) {
				return { invalidPostalCode: true };
			}

			return null;
		};
	}
}
